import baseUrl from '../utils/constant';
import { getAuthHeaders } from './authService';

export const fetchSipTrunkSettings = async () => {
  const response = await fetch(`${baseUrl}/sip-trunks/settings`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch the SIP trunk settings.');
  }

  return data.data;
};

export const saveSipTrunkSettings = async (settings) => {
  const response = await fetch(`${baseUrl}/sip-trunks/settings`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(settings),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to save the SIP trunk settings.');
  }

  return data;
};

export const fetchCallLogs = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.caller && { caller: params.caller }),
      ...(params.receiver && { receiver: params.receiver }),
      ...(params.call_type && { call_type: params.call_type }),
      ...(params.call_status && { call_status: params.call_status }),
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date })
    });

    const response = await fetch(`${baseUrl}/call-logs/?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch call logs');

    return await response.json();
  } catch (error) {
    console.error('Error fetching call logs:', error);
    throw error;
  }
};

export const fetchCallLogById = async (id) => {
  try {
    const response = await fetch(`${baseUrl}/call-logs/${id}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch call log');

    return await response.json();
  } catch (error) {
    console.error('Error fetching call log:', error);
    throw error;
  }
};

export const fetchIncomingCallLogs = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });

  const response = await fetch(`${baseUrl}/incoming-call-logs/?${queryParams.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch the incoming call logs.');
  }

  return data;
};

export const fetchLiveCalls = async () => {
  const response = await fetch(`${baseUrl}/call-logs/live`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Failed to fetch the live call counts.');
  }

  return data.data;
};

export const fetchCallStats = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date })
    });

    const response = await fetch(`${baseUrl}/call-logs/stats?${queryParams.toString()}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch call stats');

    return await response.json();
  } catch (error) {
    console.error('Error fetching call stats:', error);
    throw error;
  }
};

export const updateNidStatus = async (selectedNid, status, isVerified) => {
  try {
    const response = await fetch(`${baseUrl}/nid-verifications/update-status/${selectedNid}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status,
        is_verified: isVerified,
      }),
    });

    const data = await response.json();
    return data.success ? data : false;
  } catch (err) {
    console.error('Failed to update NID', err);
    return false;
  }
};

export const fetchNidVerifications = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.nid_no && { nid_no: params.nid_no }),
      ...(params.mobile_no && { mobile_no: params.mobile_no }),
      ...(params.status && { status: params.status }),
      ...(params.is_verified !== '' && params.is_verified !== undefined
        ? { is_verified: params.is_verified }
        : {}),
      ...(params.dob && { dob: params.dob }),
      ...(params.created_at && { created_at: params.created_at }),
      ...(params.end_at && { end_at: params.end_at }),
    });

    const response = await fetch(`${baseUrl}/nid-verifications/?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch NID verifications');

    return await response.json();
  } catch (error) {
    console.error('Error fetching NID verifications:', error);
    throw error;
  }
};

export const verifyNid = async (nidNumber) => {
  try {
    const response = await fetch(`${baseUrl}/nid-verifications/verify`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ nid_no: nidNumber }),
    });

    if (!response.ok) throw new Error('Failed to verify NID');

    return await response.json();
  } catch (error) {
    console.error('Error verifying NID:', error);
    throw error;
  }
};

export const fetchRechargeHistory = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date }),
      ...(params.min_amount && { min_amount: params.min_amount }),
      ...(params.max_amount && { max_amount: params.max_amount }),
    });

    const response = await fetch(`${baseUrl}/recharge-history/?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch recharge history');

    return await response.json();
  } catch (error) {
    console.error('Error fetching recharge history:', error);
    throw error;
  }
};

export const fetchUserAccounts = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.registration_number && { registration_number: params.registration_number }),
      ...(params.assigned_did && { assigned_did: params.assigned_did }),
      ...(params.status !== '' && params.status !== undefined && params.status !== null
        ? { status: params.status }
        : {}),
    });

    const response = await fetch(`${baseUrl}/user-accounts/?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch user accounts');

    return await response.json();
  } catch (error) {
    console.error('Error fetching user accounts:', error);
    throw error;
  }
};

export const changeUserAccountStatus = async (userId) => {
  try {
    const response = await fetch(`${baseUrl}/user-accounts/${userId}/change-status`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) throw new Error(data.message || 'Failed to change user account status');

    return data;
  } catch (error) {
    console.error('Error changing user account status:', error);
    throw error;
  }
};

export const addUserBalance = async (userId, amount, reason) => {
  try {
    const response = await fetch(`${baseUrl}/recharge-history/add-balance/${userId}`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ amount, reason }),
    });

    const data = await response.json();

    if (!response.ok) throw new Error(data.message || 'Failed to add balance');

    return data;
  } catch (error) {
    console.error('Error adding balance:', error);
    throw error;
  }
};

export const fetchUserSessions = async (params) => {
  try {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      per_page: params.per_page || 10,
      ...(params.phone && { phone: params.phone }),
      ...(params.path && { path: params.path }),
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date }),
    });

    const response = await fetch(`${baseUrl}/user-sessions/?${queryParams}`, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    if (!response.ok) throw new Error('Failed to fetch user sessions');

    return await response.json();
  } catch (error) {
    console.error('Error fetching user sessions:', error);
    throw error;
  }
};


const readJson = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || fallback);
  }
  return data;
};

export const fetchRateSheetStatus = async () => {
  const response = await fetch(`${baseUrl}/rate-sheets/status`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await readJson(response, 'Failed to fetch the rate sheet status.');
  return data.data;
};

export const fetchCurrentRates = async ({ page = 1, per_page = 50, category = '', search = '' } = {}) => {
  const params = new URLSearchParams({ page, per_page });
  if (category) params.set('category', category);
  if (search) params.set('search', search);

  const response = await fetch(`${baseUrl}/rate-sheets/current?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to fetch the current rates.');
};

export const uploadRateSheet = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  // No Content-Type here: the browser sets the multipart boundary itself.
  const response = await fetch(`${baseUrl}/rate-sheets/upload`, {
    method: 'POST',
    headers: { Authorization: getAuthHeaders().Authorization },
    body: formData,
  });
  const data = await readJson(response, 'Failed to upload the rate sheet.');
  return data.data;
};

export const activateRateVersion = async (versionId) => {
  const response = await fetch(`${baseUrl}/rate-sheets/activate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ version_id: versionId }),
  });
  return readJson(response, 'Failed to activate the rate sheet version.');
};

export const fetchRateVersions = async () => {
  const response = await fetch(`${baseUrl}/rate-sheets/versions`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await readJson(response, 'Failed to fetch the rate sheet versions.');
  return data.data;
};

export const discardRateVersion = async (versionId) => {
  const response = await fetch(`${baseUrl}/rate-sheets/versions/${versionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to discard the draft version.');
};

export const fetchStickerPacks = async () => {
  const response = await fetch(`${baseUrl}/stickers/packs`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await readJson(response, 'Failed to fetch the sticker packs.');
  return data.data;
};

export const createStickerPack = async (pack) => {
  const response = await fetch(`${baseUrl}/stickers/packs`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(pack),
  });
  return readJson(response, 'Failed to create the sticker pack.');
};

export const deleteStickerPack = async (packId) => {
  const response = await fetch(`${baseUrl}/stickers/packs/${encodeURIComponent(packId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to delete the sticker pack.');
};

export const fetchStickers = async (packId) => {
  const response = await fetch(`${baseUrl}/stickers/packs/${encodeURIComponent(packId)}/stickers`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await readJson(response, 'Failed to fetch the stickers.');
  return data.data;
};

export const createSticker = async (packId, sticker) => {
  const response = await fetch(`${baseUrl}/stickers/packs/${encodeURIComponent(packId)}/stickers`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(sticker),
  });
  return readJson(response, 'Failed to create the sticker.');
};

export const deleteSticker = async (stickerId) => {
  const response = await fetch(`${baseUrl}/stickers/${encodeURIComponent(stickerId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to delete the sticker.');
};

export const fetchChatReports = async ({ page = 1, per_page = 10 } = {}) => {
  const params = new URLSearchParams({ page, per_page });
  const response = await fetch(`${baseUrl}/chat-reports/?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to fetch the chat reports.');
};

export const deleteChatReport = async (reportId) => {
  const response = await fetch(`${baseUrl}/chat-reports/${reportId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to delete the chat report.');
};

export const deleteAllChatReports = async () => {
  const response = await fetch(`${baseUrl}/chat-reports/`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to delete the chat reports.');
};

export const fetchAccountDeletionRequests = async ({ page = 1, per_page = 10, status = '', phone = '' } = {}) => {
  const params = new URLSearchParams({ page, per_page });
  if (status) params.set('status', status);
  if (phone) params.set('phone', phone);
  const response = await fetch(`${baseUrl}/account-deletion-requests/?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to fetch the account deletion requests.');
};

export const approveAccountDeletionRequest = async (requestId, adminNote = '') => {
  const response = await fetch(`${baseUrl}/account-deletion-requests/${requestId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ admin_note: adminNote }),
  });
  return readJson(response, 'Failed to approve the account deletion request.');
};

export const rejectAccountDeletionRequest = async (requestId, adminNote = '') => {
  const response = await fetch(`${baseUrl}/account-deletion-requests/${requestId}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ admin_note: adminNote }),
  });
  return readJson(response, 'Failed to reject the account deletion request.');
};

export const fetchServiceNumbers = async () => {
  const response = await fetch(`${baseUrl}/service-numbers/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return readJson(response, 'Failed to fetch the service numbers.');
};

export const updateServiceNumber = async (name, number) => {
  const response = await fetch(`${baseUrl}/service-numbers/${name}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ number }),
  });
  return readJson(response, 'Failed to update the service number.');
};
